import { useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  Database,
  Download,
  FileSpreadsheet,
  Info,
  RefreshCcw,
  Search,
  Upload,
  Wallet,
} from 'lucide-react';

import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { MonthFilter } from '@/ui/MonthFilter';
import { Notificacao } from '@/ui/Notificacao';
import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { StatsGrid, type StatItem } from '@/ui/StatsGrid';
import { exportarCsv } from '@/ui/exportCsv';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader } from '@/ui/base/card';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { TableSkeleton } from '@/ui/base/table-skeleton';

import {
  useImportarReservasSof,
  useReservasSof,
  useSincronizarReservasSof,
} from '../hooks/useSofExtras';
import type { ReservaSofLinha } from '../types';
import { parseReservasSofArquivo, type ReservaSofImportada } from './importarReservasSof';
import { SofHeader } from './SofHeader';
import { SofTabsNav } from './SofTabsNav';

function formatarDataHora(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(
    new Date(iso),
  );
}

function competenciaAtual(): string {
  const hoje = new Date();
  return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;
}

function formatarMes(mes: string): string {
  const [ano, mesNum] = mes.split('-');
  if (!ano || !mesNum) return mes;
  const data = new Date(Number(ano), Number(mesNum) - 1, 1);
  const texto = data.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
  return texto.charAt(0).toUpperCase() + texto.slice(1).replace('.', '');
}

/** FIN-031.1: reservas orçamentárias sincronizadas com o SOF. */
export function SofReservasPage() {
  const [busca, definirBusca] = useState('');
  const [mes, definirMes] = useState(competenciaAtual());
  const [subFuncao, definirSubFuncao] = useState<string | null>(null);
  const { data, isLoading, isError, refetch } = useReservasSof(mes || undefined);
  const sincronizar = useSincronizarReservasSof();
  const importar = useImportarReservasSof();

  const [modalAberto, definirModalAberto] = useState(false);
  const [nomeArquivo, definirNomeArquivo] = useState<string | null>(null);
  const [resultado, definirResultado] = useState<{
    linhas: ReservaSofImportada[];
    erros: string[];
  } | null>(null);
  const [lendo, definirLendo] = useState(false);
  const [aviso, definirAviso] = useState<{ tom: 'sucesso' | 'falha'; mensagem: string } | null>(
    null,
  );
  const entradaArquivoRef = useRef<HTMLInputElement>(null);

  function fecharModal(aberto: boolean) {
    definirModalAberto(aberto);
    if (!aberto) {
      definirResultado(null);
      definirNomeArquivo(null);
    }
  }

  async function selecionarArquivo(arquivo: File | null) {
    if (!arquivo) return;
    definirNomeArquivo(arquivo.name);
    definirLendo(true);
    try {
      definirResultado(await parseReservasSofArquivo(arquivo));
    } catch {
      definirResultado({ linhas: [], erros: ['Não foi possível ler este arquivo.'] });
    } finally {
      definirLendo(false);
      if (entradaArquivoRef.current) entradaArquivoRef.current.value = '';
    }
  }

  async function confirmarImportacao() {
    if (!resultado || resultado.linhas.length === 0) return;
    const competencia = mes ? `${mes}-01` : new Date().toISOString().slice(0, 8) + '01';
    try {
      const { gravadas } = await importar.mutateAsync({ competencia, linhas: resultado.linhas });
      definirAviso({
        tom: 'sucesso',
        mensagem: `${gravadas} reserva(s) gravada(s) na competência.`,
      });
      fecharModal(false);
    } catch {
      definirAviso({
        tom: 'falha',
        mensagem: 'Não foi possível gravar a importação. Tente novamente.',
      });
    }
  }

  const porMes = useMemo(() => data ?? [], [data]);

  const subFuncoes = useMemo(() => {
    const contagem = new Map<string, { codigo: string; nome: string; qtd: number }>();
    for (const r of porMes) {
      const chave = r.subfuncaoCodigo || '—';
      const atual = contagem.get(chave);
      if (atual) atual.qtd += 1;
      else contagem.set(chave, { codigo: chave, nome: r.subfuncaoTexto, qtd: 1 });
    }
    return [...contagem.values()].sort((a, b) => a.codigo.localeCompare(b.codigo));
  }, [porMes]);

  const filtradas = useMemo(() => {
    let linhas = porMes;
    if (subFuncao) linhas = linhas.filter((r) => r.subfuncaoCodigo === subFuncao);
    if (busca) {
      const termo = busca.toLowerCase();
      linhas = linhas.filter(
        (r) =>
          r.dotacao.toLowerCase().includes(termo) ||
          r.programa.toLowerCase().includes(termo) ||
          r.subfuncaoTexto.toLowerCase().includes(termo),
      );
    }
    return linhas;
  }, [porMes, busca, subFuncao]);

  const ultimaSincronizacao = porMes.reduce<string | null>((maisRecente, r) => {
    if (!r.ultimaSincronizacao) return maisRecente;
    if (!maisRecente || r.ultimaSincronizacao > maisRecente) return r.ultimaSincronizacao;
    return maisRecente;
  }, null);

  const stats: StatItem[] = [
    {
      title: 'Saldo total de reserva',
      value: new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
        porMes.reduce((soma, r) => soma + parseFloat(r.saldoReserva), 0),
      ),
      description: mes ? formatarMes(mes) : 'Todas as competências',
      icon: Wallet,
    },
    {
      title: 'Reservas ativas',
      value: porMes.length,
      description: 'Linhas na competência',
      icon: Database,
    },
    {
      title: 'Dotações distintas',
      value: new Set(porMes.map((r) => r.dotacao)).size,
      description: 'Códigos formatados únicos',
      icon: FileSpreadsheet,
    },
    {
      title: 'Última sincronização',
      value: ultimaSincronizacao ? formatarDataHora(ultimaSincronizacao) : '—',
      description: ultimaSincronizacao ? undefined : 'Nenhum import registrado',
      icon: RefreshCcw,
    },
  ];

  const colunas: DataTableColumn<ReservaSofLinha>[] = [
    {
      header: 'Reserva',
      accessorKey: 'codigoReserva',
      cellClassName: 'font-mono text-xs',
      sortKey: 'codigoReserva',
    },
    { header: 'Dotação', accessorKey: 'dotacao', cellClassName: 'font-mono text-xs break-all' },
    {
      header: 'Saldo da reserva',
      cell: (linha) =>
        new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
          parseFloat(linha.saldoReserva),
        ),
      cellClassName: 'text-right font-mono tabular-nums',
      headerClassName: 'text-right',
      sortKey: (linha) => parseFloat(linha.saldoReserva),
    },
    {
      header: 'Sub-função',
      cell: (linha) => (
        <div className="flex flex-col">
          <span className="font-mono text-xs text-muted-foreground">
            {linha.subfuncaoCodigo || '—'}
          </span>
          <span>{linha.subfuncaoTexto || '—'}</span>
        </div>
      ),
    },
    { header: 'Programa', cell: (linha) => linha.programa || '—' },
    {
      header: 'Projeto/Atividade',
      cell: (linha) => linha.projetoAtividade || '—',
      cellClassName: 'font-mono text-xs',
    },
    {
      header: 'Conta despesa',
      cell: (linha) => linha.contaDespesa || '—',
      cellClassName: 'font-mono text-xs',
    },
    {
      header: 'Competência',
      sortKey: 'competencia',
      cell: (linha) =>
        new Date(`${linha.competencia}T00:00:00`).toLocaleDateString('pt-BR', {
          month: '2-digit',
          year: 'numeric',
        }),
    },
    {
      header: 'Última sincronização',
      sortKey: (linha) =>
        linha.ultimaSincronizacao ? new Date(linha.ultimaSincronizacao).getTime() : 0,
      cell: (linha) => (
        <span className="text-xs text-muted-foreground">
          {formatarDataHora(linha.ultimaSincronizacao)}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <SofHeader />

      <SofTabsNav />

      <Alert>
        <Info className="h-4 w-4" aria-hidden="true" />
        <AlertDescription className="text-xs">
          <strong>Protótipo funcional.</strong> O layout de reserva de dotação ainda aguarda a
          especificação oficial da Secretaria da Fazenda. Os valores abaixo vêm de importação de
          planilha ou sincronização simulada, não da integração real com o SOF.
        </AlertDescription>
      </Alert>

      {aviso && (
        <Notificacao
          tom={aviso.tom}
          mensagem={aviso.mensagem}
          aoFechar={() => definirAviso(null)}
        />
      )}

      <StatsGrid stats={stats} columns="grid-cols-2 lg:grid-cols-4" />

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
              <div className="min-w-[260px] flex-1 space-y-1 sm:max-w-xl">
                <Label className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Busca
                </Label>
                <div className="relative">
                  <Search
                    className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                    aria-hidden="true"
                  />
                  <Input
                    value={busca}
                    onChange={(e) => definirBusca(e.target.value)}
                    placeholder="Dotação, programa ou sub-função…"
                    className="h-10 pl-9"
                    aria-label="Buscar reserva"
                  />
                </div>
              </div>

              <div className="hidden h-10 w-px self-end bg-border/60 sm:block" aria-hidden="true" />

              <div className="space-y-1 sm:w-[170px]">
                <Label className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Competência
                </Label>
                <MonthFilter value={mes} onChange={definirMes} hideLabel />
              </div>

              <div className="space-y-1 sm:w-[220px]">
                <Label className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Sub-função
                </Label>
                <Select
                  value={subFuncao ?? '_todas'}
                  onValueChange={(v) => definirSubFuncao(v === '_todas' ? null : v)}
                >
                  <SelectTrigger className="h-10">
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_todas">Todas</SelectItem>
                    {subFuncoes.map((s) => (
                      <SelectItem key={s.codigo} value={s.codigo}>
                        {s.codigo} — {s.nome} ({s.qtd})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 lg:shrink-0 lg:border-l lg:border-border/60 lg:pl-3">
              <Button
                variant="outline"
                className="h-10"
                onClick={() => void sincronizar.mutate()}
                disabled={sincronizar.isPending}
              >
                <RefreshCcw className="mr-2 h-4 w-4" aria-hidden="true" />
                {sincronizar.isPending ? 'Sincronizando…' : 'Sincronizar com SOF'}
              </Button>
              <Button
                variant="outline"
                className="h-10"
                disabled={filtradas.length === 0}
                onClick={() =>
                  exportarCsv(
                    `reservas-sof-${mes || 'todas'}.csv`,
                    [
                      'Reserva',
                      'Dotação',
                      'Saldo da reserva',
                      'Sub-função código',
                      'Sub-função texto',
                      'Programa',
                      'Projeto/Atividade',
                      'Conta despesa',
                    ],
                    filtradas.map((r) => [
                      r.codigoReserva,
                      r.dotacao,
                      r.saldoReserva,
                      r.subfuncaoCodigo,
                      r.subfuncaoTexto,
                      r.programa,
                      r.projetoAtividade,
                      r.contaDespesa,
                    ]),
                  )
                }
              >
                <Download className="mr-2 h-4 w-4" aria-hidden="true" />
                Exportar CSV
              </Button>
              <Button className="h-10" onClick={() => definirModalAberto(true)}>
                <Upload className="mr-2 h-4 w-4" aria-hidden="true" />
                Importar planilha
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isError ? (
            <ErrorState
              variant="load-failed"
              primaryAction={{ label: 'Tentar novamente', onClick: () => void refetch() }}
            />
          ) : isLoading ? (
            <TableSkeleton columns={colunas.length} rows={6} />
          ) : filtradas.length === 0 ? (
            <EmptyState
              icon={Wallet}
              title={mes ? `Nenhuma reserva em ${formatarMes(mes)}` : 'Nenhuma reserva encontrada'}
              description={
                busca || subFuncao
                  ? 'Ajuste os filtros ou importe a planilha do SOF.'
                  : 'Importe a planilha de reservas exportada do SOF para começar.'
              }
              actionLabel="Importar planilha"
              onAction={() => definirModalAberto(true)}
            />
          ) : (
            <DataTable columns={colunas} data={filtradas} ariaLabel="Reservas de dotação SOF" />
          )}
        </CardContent>
      </Card>

      <ResponsiveModal
        open={modalAberto}
        onOpenChange={fecharModal}
        title="Importar planilha de reservas SOF"
        description={`Competência destino: ${mes ? formatarMes(mes) : 'mês corrente'}. Colunas obrigatórias: Reserva, Dotação, Saldo da reserva.`}
      >
        <div className="space-y-4">
          <div className="rounded-lg border border-dashed border-border bg-muted/30 p-6 text-center">
            <FileSpreadsheet
              className="mx-auto h-10 w-10 text-muted-foreground"
              aria-hidden="true"
            />
            <p className="mt-2 text-sm font-medium">
              {nomeArquivo ?? 'Selecione um arquivo .xlsx ou .csv'}
            </p>
            <input
              ref={entradaArquivoRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={(e) => void selecionarArquivo(e.target.files?.[0] ?? null)}
            />
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => entradaArquivoRef.current?.click()}
              disabled={lendo}
            >
              <Upload className="mr-2 h-4 w-4" aria-hidden="true" />
              {nomeArquivo ? 'Trocar arquivo' : 'Selecionar arquivo'}
            </Button>
          </div>

          {resultado && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <Badge variant="secondary">{resultado.linhas.length} linha(s) válida(s)</Badge>
                {resultado.erros.length > 0 && (
                  <Badge variant="destructive">{resultado.erros.length} erro(s)</Badge>
                )}
              </div>

              {resultado.erros.length > 0 && (
                <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm">
                  <AlertTriangle
                    className="mt-0.5 h-4 w-4 shrink-0 text-destructive"
                    aria-hidden="true"
                  />
                  <ul className="list-disc space-y-0.5 pl-4 text-xs">
                    {resultado.erros.slice(0, 10).map((erro) => (
                      <li key={erro}>{erro}</li>
                    ))}
                  </ul>
                </div>
              )}

              {resultado.linhas.length > 0 && (
                <div className="overflow-x-auto rounded-lg border">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50 text-muted-foreground">
                      <tr>
                        <th className="px-2 py-1 text-left">Reserva</th>
                        <th className="px-2 py-1 text-left">Dotação</th>
                        <th className="px-2 py-1 text-right">Saldo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resultado.linhas.slice(0, 10).map((linha) => (
                        <tr key={linha.codigoReserva} className="border-t">
                          <td className="px-2 py-1 font-mono">{linha.codigoReserva}</td>
                          <td className="px-2 py-1 font-mono">{linha.dotacao}</td>
                          <td className="px-2 py-1 text-right font-mono tabular-nums">
                            {linha.saldoReserva}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {resultado.linhas.length > 10 && (
                    <p className="px-2 py-1 text-[11px] text-muted-foreground">
                      Pré-visualizando 10 de {resultado.linhas.length} linhas.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => fecharModal(false)}
              disabled={importar.isPending}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => void confirmarImportacao()}
              disabled={!resultado || resultado.linhas.length === 0 || importar.isPending}
            >
              {importar.isPending
                ? 'Gravando…'
                : `Confirmar ${resultado?.linhas.length ?? 0} reserva(s)`}
            </Button>
          </div>
        </div>
      </ResponsiveModal>
    </div>
  );
}

export default SofReservasPage;
